import {materialEstimateUnitStates} from "../../data/MaterialEstimateUnitState";

export const getMaterialEstimateArticle = (row) =>
    row.materialOrganizationOption?.articleNumber || row.tempArticle || "";

export const getMaterialEstimateName = (row) =>
    row.materialOrganizationOption?.name || row.tempName || "";

export const getMaterialEstimateUnitStateLabel = (row) =>
    materialEstimateUnitStates[row.materialEstimateUnitState] ?? row.materialEstimateUnitState ?? "";

/** Same text as in the «Заявка» column (not only requestsCutting.number). */
export const getMaterialEstimateRequestsCuttingLabel = (row) => {
    const material = row.materialOrganizationOption;
    if (!material) {
        return "";
    }
    if (material.typeMaterial === "SLAB") {
        return row.requestsCutting?.number ?? "Ожидание";
    }
    if (material.subTypeMaterial === "EDGE") {
        return "Кромочный";
    }
    return "Фурнитура";
};

/**
 * Catalog-only sort keys (ignore temp*). Unmatched / red rows sort as empty
 * and group at the start or end — same as before the search accessors.
 * Display and global search still use getMaterialEstimate* above.
 */
const compareOptionalSortStrings = (a, b) => {
    const aVal = a ?? "";
    const bVal = b ?? "";
    const aEmpty = aVal === "";
    const bEmpty = bVal === "";
    if (aEmpty && bEmpty) {
        return 0;
    }
    if (aEmpty) {
        return -1;
    }
    if (bEmpty) {
        return 1;
    }
    return aVal.localeCompare(bVal, "ru", {numeric: true, sensitivity: "base"});
};

export const sortMaterialEstimateByCatalogArticle = (rowA, rowB) =>
    compareOptionalSortStrings(
        rowA.original.materialOrganizationOption?.articleNumber,
        rowB.original.materialOrganizationOption?.articleNumber,
    );

export const sortMaterialEstimateByCatalogName = (rowA, rowB) =>
    compareOptionalSortStrings(
        rowA.original.materialOrganizationOption?.name,
        rowB.original.materialOrganizationOption?.name,
    );

/** Sort «Заявка» by cutting number only (furniture / red without number group together). */
export const sortMaterialEstimateByRequestsCuttingNumber = (rowA, rowB) =>
    compareOptionalSortStrings(
        rowA.original.requestsCutting?.number,
        rowB.original.requestsCutting?.number,
    );

/**
 * MRT/TanStack by default only global-filters columns whose first-row value is
 * string|number. Estimate tables store prices as `{ price }` objects, so search
 * would skip them (and often skip everything). Allow all accessor columns.
 */
export const estimateGetColumnCanGlobalFilter = () => true;

/**
 * Global search over cell values, including price objects and numeric fields.
 * Must be registered via `filterFns` + string `globalFilterFn` name — MRT
 * overwrites a function passed directly as `globalFilterFn` and filtering breaks.
 *
 * Numbers are matched in the same display format as in the UI / Economy:
 * - empty → "ожидание" (and "-" where UI shows dash)
 * - money / margins → toFixed(2)
 * - quantity → toFixed(3) (and toFixed(2) where that table uses it)
 * - text → case-insensitive includes
 *
 * Matching is done on the whole row (not only the current column): TanStack calls
 * the global filter once per column with OR semantics, and empty cells used to
 * false-positive on short queries via `"ожидание".includes(search)`.
 */
export const ESTIMATE_GLOBAL_FILTER_FN = "estimateContains";

const QUANTITY_COLUMN_IDS = new Set(["quantity"]);

const PRICE_FIELD_KEYS = [
    "basePrice",
    "alternativePrice",
    "baseJobPrice",
    "alternativeJobPrice",
    "baseTotalPrice",
    "alternativeTotalPrice",
    "costBasePrice",
    "costAlternativePrice",
    "costBaseJobPrice",
    "costAlternativeJobPrice",
    "totalCostBasePrice",
    "totalCostAlternativePrice",
    "profitBaseMaterialPrice",
    "profitAlternativeMaterialPrice",
    "profitBaseJobPrice",
    "profitAlternativeJobPrice",
    "profitBaseTotalPrice",
    "profitAlternativeTotalPrice",
    "calculationCostBasePrice",
    "calculationCostBaseJobPrice",
    "calculationTotalCostBasePrice",
    "profitCalculationBaseMaterialPrice",
    "profitCalculationBaseJobPrice",
    "profitCalculationBaseTotalPrice",
    "baseWorth",
    "alternativeWorth",
    "costInOrderBasePrice",
    "costInOrderAlternativePrice",
];

/** Empty/"Ожидание" only for deliberate queries — not every letter in «ожидание». */
const matchEmptyNumericDisplay = (search) =>
    search === "-" || (search.length >= 3 && "ожидание".includes(search));

const matchFormattedNumber = (value, search, fractionDigits) => {
    if (value == null || value === "") {
        return matchEmptyNumericDisplay(search);
    }
    if (typeof value !== "number" || Number.isNaN(value)) {
        return false;
    }
    return value.toFixed(fractionDigits).includes(search);
};

const pushSearchText = (texts, value) => {
    if (value == null || value === "") {
        return;
    }
    texts.push(String(value));
};

const pushPriceSearchText = (texts, value) => {
    if (value == null || value === "") {
        return;
    }
    if (typeof value === "object") {
        if (typeof value.price === "number") {
            texts.push(value.price.toFixed(2));
        }
        return;
    }
    if (typeof value === "number" && !Number.isNaN(value)) {
        texts.push(value.toFixed(2));
    }
};

/** Collect display strings from an estimate / job / production-cost row. */
export const collectEstimateSearchTexts = (row) => {
    if (!row || typeof row !== "object") {
        return [];
    }

    const texts = [];

    pushSearchText(texts, getMaterialEstimateArticle(row));
    pushSearchText(texts, getMaterialEstimateName(row));
    pushSearchText(texts, getMaterialEstimateRequestsCuttingLabel(row));
    pushSearchText(texts, getMaterialEstimateUnitStateLabel(row));
    pushSearchText(texts, row.unit?.name);
    pushSearchText(texts, row.tempUnit?.name);
    pushSearchText(texts, row.note);
    pushSearchText(texts, row.materialName);
    pushSearchText(texts, row.metricName);
    pushSearchText(texts, row.unitName);
    pushSearchText(texts, row.signJob?.name);
    pushSearchText(texts, row.modelParameterUnit);

    if (typeof row.quantity === "number" && !Number.isNaN(row.quantity)) {
        texts.push(row.quantity.toFixed(3));
        texts.push(row.quantity.toFixed(2));
    }

    if (row.reservePercentage != null && row.reservePercentage !== "") {
        texts.push(String(row.reservePercentage));
    }

    if (typeof row.grossMargin === "number" && !Number.isNaN(row.grossMargin)) {
        texts.push(row.grossMargin.toFixed(2));
    }

    for (const key of PRICE_FIELD_KEYS) {
        pushPriceSearchText(texts, row[key]);
    }

    return texts;
};

const cellValueMatchesSearch = (value, columnId, search) => {
    if (value == null || value === "") {
        return matchEmptyNumericDisplay(search);
    }

    if (typeof value === "object") {
        return matchFormattedNumber(value.price, search, 2);
    }

    if (typeof value === "number") {
        if (QUANTITY_COLUMN_IDS.has(columnId)) {
            return value.toFixed(3).includes(search) || value.toFixed(2).includes(search);
        }
        return matchFormattedNumber(value, search, 2);
    }

    return value.toString().toLowerCase().includes(search);
};

export const estimateRowMatchesGlobalSearch = (row, filterValue) => {
    const search = filterValue?.toString()?.toLowerCase?.() ?? "";
    if (!search) {
        return true;
    }
    return collectEstimateSearchTexts(row).some((text) => text.toLowerCase().includes(search));
};

export const filterEstimateRowsByGlobalSearch = (rows, filterValue) => {
    const search = filterValue?.toString()?.toLowerCase?.() ?? "";
    if (!search) {
        return rows ?? [];
    }
    return (rows ?? []).filter((row) => estimateRowMatchesGlobalSearch(row, search));
};

export const estimateGlobalFilterFn = (row, columnId, filterValue) => {
    const search = filterValue?.toString()?.toLowerCase?.() ?? "";
    if (!search) {
        return true;
    }

    if (estimateRowMatchesGlobalSearch(row.original, search)) {
        return true;
    }

    // Fallback for columns / shapes not covered by collectEstimateSearchTexts
    return cellValueMatchesSearch(row.getValue(columnId), columnId, search);
};

export const estimateFilterFns = {
    [ESTIMATE_GLOBAL_FILTER_FN]: estimateGlobalFilterFn,
};
